/**
 * Validation logic for the fix-errors page.
 * Determines which modal to show based on error classification.
 */

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';

import type { ErrorCell } from '../utils/fixErrorFieldMaps';
import {
  PROPERTY_OPTIONAL_FIELDS,
  ORDER_OPTIONAL_FIELDS,
  PROPERTY_MANDATORY_FIELDS,
  ORDER_MANDATORY_FIELDS,
  allRowsHaveMandatoryErrors,
  hasActiveFieldInList,
  allRowsFullyInactive,
  anyRowFullyInactive,
} from '../utils/fixErrorFieldMaps';
import type { FixErrorModal } from '../types/modal-state';

// ---- Types ----------------------------------------------------------------

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

interface ValidationCallbacks {
  setModal: (state: FixErrorModal) => void;
}

// ---- Hook -----------------------------------------------------------------

export const useFixErrorValidation = (
  isProperty: boolean,
  callbacks: ValidationCallbacks
) => {
  const dispatch = useDispatch();

  const validateForOptionalFields = (
    data: ErrorTableRow[]
  ): boolean => {
    return data.some((order) =>
      Object.keys(order).some(
        (key) =>
          (ORDER_OPTIONAL_FIELDS as readonly string[]).includes(key) &&
          (order[key] as ErrorCell)?.isActive === true
      )
    );
  };

  const hasActiveItem = useCallback(
    (data: ErrorTableRow[] | string | null): boolean => {
      if (!data) return false;

      if (!isProperty) {
        let parsedData: ErrorTableRow[];
        if (typeof data === 'string') {
          parsedData = JSON.parse(data);
        } else {
          parsedData = data;
        }

        if (parsedData.length === 1) {
          const hasNonOptionalError = parsedData.some((item) =>
            Object.values(item).some(
              (sub) =>
                typeof sub === 'object' &&
                sub !== null &&
                (sub as ErrorCell).isActive === true &&
                (sub as ErrorCell).isOptionalField !== true
            )
          );
          const hasOptionalError = parsedData.some((item) =>
            Object.values(item).some(
              (sub) =>
                typeof sub === 'object' &&
                sub !== null &&
                (sub as ErrorCell).isActive === true &&
                (sub as ErrorCell).isOptionalField === true
            )
          );

          if (hasNonOptionalError) {
            callbacks.setModal({ kind: 'all-mandatory' });
            return true;
          }
          if (hasOptionalError) {
            callbacks.setModal({ kind: 'order-optional' });
            return true;
          }
          return false;
        }

        if (parsedData.length > 1) {
          const allHaveMandatory = allRowsHaveMandatoryErrors(
            parsedData,
            ORDER_MANDATORY_FIELDS
          );

          if (allHaveMandatory) {
            callbacks.setModal({ kind: 'all-mandatory' });
            return true;
          }

          const allInactive = allRowsFullyInactive(
            parsedData,
            ORDER_MANDATORY_FIELDS
          );
          const anyOneInactive = anyRowFullyInactive(
            parsedData,
            ORDER_MANDATORY_FIELDS
          );

          if (allInactive) {
            if (
              hasActiveFieldInList(
                parsedData,
                ORDER_OPTIONAL_FIELDS
              )
            ) {
              callbacks.setModal({ kind: 'order-optional' });
              return true;
            }
            return false;
          }

          if (anyOneInactive) {
            if (validateForOptionalFields(parsedData)) {
              callbacks.setModal({ kind: 'order-partial-optional' });
              return true;
            }
            return false;
          }
        }

        return false;
      }

      // Property flow
      const propertyData = data as ErrorTableRow[];
      const allMandatoryFailed = propertyData.every((property) =>
        (PROPERTY_MANDATORY_FIELDS as readonly string[]).some(
          (field) => (property[field] as ErrorCell)?.isActive === true
        )
      );

      if (allMandatoryFailed) {
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Please fix all the errors for Required fields to import the properties',
          })
        );
        return true;
      }

      if (
        hasActiveFieldInList(
          propertyData,
          PROPERTY_OPTIONAL_FIELDS
        )
      ) {
        callbacks.setModal({ kind: 'property-optional' });
        return true;
      }

      return false;
    },
    [isProperty, dispatch, callbacks]
  );

  return { hasActiveItem };
};
